import { createIngredient } from '../../types/Ingredient.ts';
import Recipe from '../../types/Recipe.ts';
import { Unit } from '../../types/Unit.ts';

const dessertsRecipes: Recipe[] = [
  {
    name: "Brownies",
    picture: "/brownie.jpg",
    alt_text: "A stack of 3 fudgy brownies with a few more scattered around them.",
    description: `Fudgy, moist, chocolatey brownies, that in my grandma's words, are "to die for."`,
    rating: 5,
    ingredients: [ 
        createIngredient("All-purpose flour", 1, new Unit("cup")),
        createIngredient("Cocoa powder", .75, new Unit("cup")), 
        createIngredient("Sugar", 2, new Unit("cup")), 
        createIngredient("Butter", 1, new Unit("cup")),
        createIngredient("Egg", 3, new Unit("egg")),
        createIngredient("Vanilla extract", 1, new Unit("tablespoon")),
        createIngredient("Chocolate", 1.5, new Unit("cup")),
    ],
    directions: [
        "Line 8x8 baking pan with parchment paper.", "Melt butter.",
        "Whisk sugar and cocoa into the butter.", "Whisk in eggs and vanilla.", "Chop chocolate if using a bar.",
        "Add chocolate and flour and stir until just combined.", "Spread batter into pan and bake for 40-45 minutes at 350°F."
    ],
    notes: [
      "I reduced the sugar from the original recipe.",
      "I used a caramel-filled chocolate bar for 2/3 of the chocolate and semi-sweet chocolate chips for the rest.", 
      "Cooked for 5 minutes longer than the max on the original recipe."
    ],
    source: "https://preppykitchen.com/brownie-recipe/",
  },
  {
    name: "Cinnamon rolls",
    picture: "/cinnamon-roll.jpg",
    alt_text: "An overhead shot of frosted cinnamon rolls still stuck together from baking.",
    rating: 4,
    description: "A soft bread filled with cinnamon sugar and covered with sweet cream cheese frosting.",
    ingredients: [ 
        createIngredient("Bread flour", 3, new Unit("cup")),
        createIngredient("Milk", 0.75, new Unit("cup"), "Warmed to 110°F"),
        createIngredient("Yeast", 2.25, new Unit("teaspoon")),
        createIngredient("Sugar", 0.25, new Unit("cup")),
        createIngredient("Egg", 2, new Unit("egg")),
        createIngredient("Butter", 1/4, new Unit("cup")),
        createIngredient("Brown sugar", 2/3, new Unit("cup"), "For filling"),
        createIngredient("Cinnamon", 1.5, new Unit("tablespoon"), "For filling"),
        createIngredient("Butter", .25, new Unit("cup"), "For filling (softened)"),
        createIngredient("Cream cheese", 4, new Unit("ounce"), "For frosting (softened)"),
        createIngredient("Butter", 3, new Unit("tablespoon"), "For frosting (softened)"),
        createIngredient("Powdered sugar", 3/4, new Unit("cup"), "For frosting"),
        createIngredient("Vanilla extract", 1/2, new Unit("teaspoon"), "For frosting"),
    ],
    directions: [
        "Add yeast to warm milk.",
        "Add sugar, eggs, and melted butter and combine well.",
        "Stir in flour then knead dough for 8 minutes.",
        "Let dough rest covered in oiled bowl for 1 hour until doubled in size.",
        "Roll dough into 14x9 inch rectangle.",
        "Spread softened filling butter over dough leaving a quarter inch on one of the short sides.",
        "Mix fill brown sugar and cinnamon and sprinkle over dough.",
        "Tightly roll dough starting on short side.",
        "Cut roll into 9 sections.",
        "Place rolls into a 9x9 parchment paper lined baking dish.",
        "Bake for 25-30 minutes at 350°F.",
        "Beat frosting ingredients together on frost rolls after letting them cool for 5-10 minutes."
    ],
    notes: [
      "To make lemon cinnamon rolls, just add 2 Tb lemon juice to dough, 1 Tb to filling, and 3-4 Tb to frosting.",
    ],
    source: "https://www.ambitiouskitchen.com/best-cinnamon-rolls/",
  },
  {
    name: "Monkey Bread",
    picture: "/monkey-bread.jpg",
    alt_text: "Monkey bread in bundt cake shape dripping with glaze.",
    rating: 3.5,
    description: "Cinnamon-sugary finger food.",
    ingredients: [ 
        createIngredient("All-purpose flour", 4, new Unit("cup")),
        createIngredient("Sugar", 0.25, new Unit("cup")),
        createIngredient("Cornstarch", 2, new Unit("tablespoon")),
        createIngredient("Baking powder", 4, new Unit("teaspoon")),
        createIngredient("Butter", 12, new Unit("tablespoon"), "cold"),
        createIngredient("Milk", 5/4, new Unit("cup")),
        createIngredient("Sugar", 1/2, new Unit("cup"), "For rolling"),
        createIngredient("Cinnamon", 2.5, new Unit("teaspoon"), "For rolling"),
        createIngredient("Butter", 6, new Unit("tablespoon"), "For glaze"),
        createIngredient("Brown sugar", 1/3, new Unit("cup"), "For glaze"),
    ],
    directions: [
      "In a large bowl, whisk together flour, sugar, cornstarch, baking powder, and salt.",
      "Chop butter into small peices and stir into flour mixture.",
      "Add milk and stir until coheesive. May need to use hands.",
      "On a floured surface, flatten dough to 1/2 inch.",
      "Cut dough into peices smaller than 1 inch squared.",
      "Mix the cinnamon and sugar for rolling, then roll the peices in mixture and layer into pan.",
      "In a small saucepan, combine glaze ingredients over medium heat until butter is melted. Bring to boil then remove from heat.",
      "Drizzle glaze over dough.",
      "Bake for 35 minutes at 350°F."
    ],
    notes: [
      "I used a third of the glaze the original recipe used and it was plenty.",
      "I baked the monkey bread in an 8x8 pan and for 8 minutes longer suggested and the middle was still a little undercooked, so bake it in something with smaller width like a bundt pan or cupcake tin."
    ],
    source: "https://sugarspunrun.com/drunken-monkey-bread/#recipe",
  },
  {
    name: "Baked Donut",
    picture: "/baked-donut.jpg",
    alt_text: "A pile of cinnamon sugar donuts.",
    rating: 2.5,
    description: "A baked cake donut.",
    ingredients: [ 
        createIngredient("All-purpose flour", 2.33, new Unit("cup")),
        createIngredient("Sugar", .5, new Unit("cup")),
        createIngredient("Baking powder", 2.5, new Unit("teaspoon")),
        createIngredient("Cinnamon", .5, new Unit("teaspoon")),
        createIngredient("Nutmeg", .25, new Unit("teaspoon")),
        createIngredient("Milk", 1.5, new Unit("cup")),
        createIngredient("Egg", 1, new Unit("egg"), "Room tempurature"),
        createIngredient("Butter", 2, new Unit("tablespoon")),
        createIngredient("Vegetable oil", 2, new Unit("tablespoon")),
        createIngredient("Vanilla", 2, new Unit("teaspoon")),

        createIngredient("Sugar", .5, new Unit("cup"), "For toppings"),
        createIngredient("Cinnamon", 1, new Unit("tablespoon"), "For toppings"),
    ],
    directions: [
      "In a large mixing bowl, whisk together the flour sugar, baking powder, cinnamon, salt, and nutmeg.",
      "Add the buttermilk, egg, butter, vegetable oil, and vanilla.",
      "Pour or spoon batter into pan.",
      "Bake at 400°F for 10 minutes.",
      "Let cool then lightly brush with melted butter and toss in cinnamon sugar."
    ],
    notes: [
      "I had hoped that this recipe would be think enough to pipe onto a baking sheet, but it was not. It was liquid and required a sheet with forms for the batter.",
      "They were decent when fresh, but did not hold well even after just one day.",
      "I did not make the glaze from the original recipe, only used cinnamon sugar as a topping."
    ],
    source: "https://preppykitchen.com/baked-donut-recipe/#recipe",
  },
  {
    name: "Caramel Molasses Cookies",
    picture: "/caramel-molasses.jpg",
    alt_text: "Molasses cookies with caramel drizzle on a cooling rack.",
    rating: 4,
    description: "Molasses cookies with a homemade caramel sauce!",
    ingredients: [ 
      createIngredient("All-purpose flour", 3, new Unit("cup")),
      createIngredient("Baking soda", 1, new Unit("teaspoon")),
      createIngredient("Ground ginger", 2, new Unit("teaspoon")),
      createIngredient("Ground cinnamon", 1.25, new Unit("teaspoon")),
      createIngredient("Ground cloves", .25, new Unit("teaspoon")),
      createIngredient("Nutmeg", .25, new Unit("teaspoon")),
      createIngredient("Butter", .75, new Unit("cup"), "softened to room tempurature"),
      createIngredient("Brown sugar", 3/4, new Unit("cup")),
      createIngredient("Molasses", 1/3, new Unit("cup")),
      createIngredient("Egg", 1, new Unit("egg")),
      createIngredient("Vanilla", 2, new Unit("teaspoon")),

      createIngredient("Sugar", 1/3, new Unit("cup"), "For rolling"),

      createIngredient("Sugar", 1, new Unit("cup"), "For caramel sauce"),
      createIngredient("Butter", 6, new Unit("tablespoon"), "For caramel sauce"),
      createIngredient("Whipping cream", .5, new Unit("cup"), "For caramel sauce"),
    ],
    directions: [
      "Mix together the flour, baking soda, ginger, cinnamon, cloves, and nutmeg.",
      "Cream the softened butter for about 1 minute on medium speed. Add the brown sugar and beat until light and fluffy, about 2 full minutes.",
      "Add the molasses, egg, and vanilla.",
      "Mix all ingredients together.",
      "Cover dough tightly and let rest in the fridge, while making caramel sauce.",
      "Heat the sugar for the caramel sauce in a medium saucepan over medium heat, stirring constantly with a rubber spatula. Sugar will form clumps and eventually melt into a thick brown, amber-colored liquid as you continue to stir. Be careful not to burn. ",
      "Once sugar is completely melted, immediately add the butter. Be careful in this step because the caramel will bubble rapidly when the butter is added. Stir the butter into the caramel until it is completely melted, about 2-3 minutes.",
      "Very slowly, drizzle in ½ cup of heavy cream. Since the heavy cream is colder than the caramel, the mixture will rapidly bubble and/or splatter when added.",
      "Allow the mixture to boil for 1 minute then remove from heat and let cool.",
      "Preheat over to 350°F.",
      "Divide the dough into 20 peices and roll them into spheres.",
      "Roll the spheres in sugar and place on a parchment lined baking sheet.",
      "Bake for 9 minutes then pull out of over and gently pat cookies down with a fork. Then bake for 2-3 more minutes.",
      "Let cool on the baking sheet for 5 minutes before transferring to cooling rack.",
      "Once fully cooled, warm up caramel sauce for about 8-10 seconds in the microwave and drizzle over the cookies.",
      "Optionally, sprinkle the cookies with salt."
    ],
    notes: [
      "The caramel sauce is fantastic and makes the cookies themsleves so much better.",
      "The caramel sauce recipe makes way more than needed. Could cut the recipe in half or probably thirds.",
      "If making the full caramel sauce recipe, definetly use a medium sauce pan. The sauce almost boiled over a few times when I made it with a small one.",
      "I sprinkled salt over the caramel drizzle, which I think was a nice addition."
    ],
    source: "https://thetoughcookie.com/2013/12/06/caramel-molasses-cookies-with-a-salted-caramel-drizzle/",
  },
  {
    name: "S'betters",
    picture: "/sbetters.jpg",
    alt_text: "A hand holding a sbetter. Can see the cross-section of graham cracker, frosting, and chocolate.",
    rating: 3.5,
    description: "They're not S'mores, they're S'better.",
    ingredients: [ 
      createIngredient("Graham cracker", 2, new Unit("piece")),
      createIngredient("Chocolate", 2, new Unit("piece")),
      createIngredient("Vanilla frosting", 1, new Unit("tablespoon")),
    ],
    directions: [
      "Spread the frosting on one cracker.",
      "Place the chocolate on the frosted cracker",
      "Place the other cracker on the chocolate."
    ],
  },
  {
    name: "Peanut Butter Cookies",
    picture: "/peanut-butter-cookie.png",
    alt_text: "Heart-shaped peanut butter cookies on cooling rack.",
    rating: 4.5,
    description: "Soft or crispy...? You decide.",
    ingredients: [ 
        createIngredient("Butter", .5, new Unit("cup"), "Softened"),
        createIngredient("Sugar", .5, new Unit("cup")),
        createIngredient("Brown sugar", .5, new Unit("cup")),
        createIngredient("Egg", 1, new Unit("egg"), "Room tempurature"),
        createIngredient("Vanilla", 1, new Unit("teaspoon")),
        createIngredient("Peanut butter", 0.5, new Unit("cup")),
        createIngredient("All-purpose flour", 1.5, new Unit("cup")),
        createIngredient("Baking soda", .5, new Unit("teaspoon")),
        createIngredient("Baking powder", .5, new Unit("teaspoon")),
        createIngredient("Sugar", 2, new Unit("tablespoon"), "For rolling"),
    ],
    directions: [
      "Cream the softened butter and sugars together.",
      "Add and mix eggs. Then add and mix vanilla and peanut butter.",
      "Add and mix the flour, baking soda, and baking powder.",
      "Preheat oven tp 350 degrees.",
      "Roll cookies in sugar and press with fork for pattern.",
      "Bake for 12 minutes for soft cookies. Bake for 17 minutes for crisp cookies."
    ],
    notes: ["Idea: dip half the cookie in chocolate."],
    source: "https://sallysbakingaddiction.com/classic-peanut-butter-cookies/#tasty-recipes-74968",
  },
  {
    name: "Chocolate Cake",
    picture: "/choc-cake.png",
    alt_text: "Side view of a slice of cake.",
    rating: 2.5,
    description: "A very chocolatey cake that's a little dry, but I might have over baked it.",
    ingredients: [ 
        createIngredient("All-purpose flour", 1.75, new Unit("cup")),
        createIngredient("Cocoa powder", .75, new Unit("cup")),
        createIngredient("Sugar", 1.75, new Unit("cup")),
        createIngredient("Baking soda", 2, new Unit("teaspoon")),
        createIngredient("Baking powder", 1, new Unit("teaspoon")),
        createIngredient("Salt", 1, new Unit("teaspoon")),
        createIngredient("Vegetable oil", .5, new Unit("cup")),
        createIngredient("Egg", 2, new Unit("egg"), "Room tempurature"),
        createIngredient("Vanilla", 2, new Unit("teaspoon")),
        createIngredient("Milk", 1, new Unit("cup"), "Room temperature"),
        createIngredient("Coffee", 1, new Unit("cup"), "Fresh and hot")
    ],
    directions: [
      "Make coffee",
      "Preheat oven to 350°F (177°C). Grease two 9-inch cake pans. I used a 9x14 rectangular cake pan and didn't stack.",
      "Whisk the flour, cocoa powder, sugar, baking soda, baking powder, and salt.",
      "Add and mix the oil, eggs, and vanilla together.",
      "Add and mix the milk",
      "Add and mix the coffee",
      "Put in pans and bake for 23-26 minutes or until a toothpick inserted in the center comes out clean"
    ],
    notes: ["I didn't use the provided chocolate buttercream recipe, and I added coffee to the one I made."],
    source: "https://sallysbakingaddiction.com/triple-chocolate-layer-cake/#tasty-recipes-68103"
  },
  {
    name: "Lemon Cookies",
    picture: "/lemon-cookie.png",
    alt_text: "Side view of stack of cookies. Whole on the botton and a crosssection on top showing airy, soft texture.",
    rating: 5,
    description: "Super soft and perfectly lemony!",
    ingredients: [ 
        createIngredient("Butter", .5, new Unit("cup"), "Softened"),
        createIngredient("Cream cheese", 8, new Unit("ounce"), "Softened"),
        createIngredient("Sugar", 1.5, new Unit("cup")),
        createIngredient("Egg", 1, new Unit("egg")),
        createIngredient("Vanilla", 1 ,new Unit("teaspoon")),
        createIngredient("Lemon extract", 1 ,new Unit("teaspoon")),
        createIngredient("All-purpose flour", 2.25, new Unit("cup")),
        createIngredient("Powered sugar", .5, new Unit("cup")),
        createIngredient("Baking powder", 3, new Unit("teaspoon")),
        createIngredient("Powered sugar", .5, new Unit("cup"), "For rolling"),

      ],
    directions: [
      "Preheat oven to 350°F.",
      "Beat beat butter, cream cheese, and granulated sugar.",
      "Add in egg, vanilla, and lemon extract.",
      "Add in flour, baking powder, and ½ cup confectioners sugar. Beat into creamed mixture.",
      "Using a 2-tablespoon cookie scoop, scoop dough and then roll in the remaining ½ cup of confectioners’ sugar.",
      "Place on cookie sheet lined with parchment paper.",
      "Bake until they no longer appear wet on top, 8-11 minutes. After about 5 minutes cooling on the pan (they will be soft!), place on cooling racks to cool completely."
    ],
    notes: ["I didn't make these, but my I tried them and they were delicious"],
    source: "https://iambaker.net/ooey-gooey-lemon-cookies/#wprm-recipe-container-76858"
  },
  {
    name: "Pumpkin Bread",
    picture: "/pumpkin-bread.png",
    alt_text: "Sliced pumpkin bread on a cutting board surrounded by pumpkins.",
    rating: 5,
    description: "Moist, spiced, sweet, delicious.",
    ingredients: [ 
        createIngredient("Flour", 3.5, new Unit("cup")),
        createIngredient("Sugar", 3, new Unit("cup")),
        createIngredient("Baking soda", 2, new Unit("teaspoon")),
        createIngredient("Salt", 2, new Unit("teaspoon")),
        createIngredient("Ground allspice", 2, new Unit("teaspoon")),
        createIngredient("Baking powder", 1, new Unit("teaspoon")),
        createIngredient("Groud nutmeg", 1, new Unit("teaspoon")),
        createIngredient("Ground cinnamon", 1, new Unit("teaspoon")),
        createIngredient("Ground cloves", .5, new Unit("teaspoon")),
        createIngredient("Eggs", 4, new Unit("egg")),
        createIngredient("Canned solid pack pumkin", 15, new Unit("ounce")),
        createIngredient("Vegatable oil", 3/4, new Unit("cup")),
        createIngredient("Water", 2/3, new Unit("cup")),
      ],
    directions: [
      "In a large bowl combine the dry ingredients.",
      "In another bowl combine the pumpkin, eggs, oil, and water. Mix well.",
      "Stir wet into dry ingredients until just combined.",
      "Pour into 3 greased 8x4x2 inch loaf pans.",
      "Bake at 350 deg for 50-60 minutes or until a toothpick inserted near the center comes back clean.",
      "Cool for 10 minutes before removeing from pans to cooling rack."
    ],
    notes: ["Grammy's recipe",
      "Yeilds 3 loaves"
    ],
  },
];

export default dessertsRecipes;
